import { useOrganizationsList } from "../api";
import { OrganizationTreeNode } from "./OrganizationTreeNode";
import type { Organization } from "../types";

interface OrganizationTreeViewProps {
  selectedId: string | null;
  onSelect: (org: Organization) => void;
}

/**
 * Uses `GET /organizations/?roots_only=true` (present in the current
 * organization_views.py `get_queryset` — excludes any organization that
 * appears as a child in OrganizationNode) so the top level of this tree is
 * genuinely just root organizations, each lazily expanding its own children
 * via OrganizationTreeNode. This replaces an earlier draft of this
 * component that rendered the full flat list at depth 0 (which duplicated
 * child organizations) — `roots_only` did not exist when that draft was
 * written; it does now, so use it.
 */
export function OrganizationTreeView({ selectedId, onSelect }: OrganizationTreeViewProps) {
  const { data: organizations, isLoading, isError } = useOrganizationsList(true);

  if (isLoading) return <div className="text-sm text-gray-500">در حال بارگذاری سلسله‌مراتب...</div>;
  if (isError) return <div className="text-sm text-red-500">خطا در دریافت فهرست سازمان‌ها.</div>;
  if (!organizations || organizations.length === 0) {
    return <div className="text-sm text-gray-500">هنوز هیچ سازمانی ثبت نشده است.</div>;
  }

  return (
    <div role="tree" aria-label="سلسله‌مراتب سازمانی">
      {organizations.map((org: Organization) => (
        <OrganizationTreeNode
          key={org.id}
          organization={org}
          depth={0}
          selectedId={selectedId}
          onSelect={onSelect}
        />
      ))}
    </div>
  );
}
